import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { AlertTriangle, CheckCircle2, ImageIcon, Info, Loader2, Sparkles, Trash2, Upload } from "lucide-react";

/* Phase E.1 — Panneau Images du ContentParentDetail.
   Le backend E.1 est la SOURCE DE VÉRITÉ : aucune heuristique ALT, aucun calcul de
   statut métier, aucune validité shared/localized recalculée ici. React affiche,
   saisit, appelle les endpoints et rend les diagnostics backend tels quels. */

const SOURCE_LABELS = {
  AI_GENERATED: "Générée par IA",
  UPLOADED: "Téléversée",
  LEGACY_REFERENCE: "Référence historique (ancienne couverture)",
};
const STATUS_LABELS = { GENERATING: "Génération en cours", READY: "Prête", ERROR: "Erreur" };
const STATUS_STYLES = {
  GENERATING: "bg-blue-100 text-blue-700",
  READY: "bg-emerald-100 text-emerald-700",
  ERROR: "bg-red-100 text-red-700",
};

const fmtSize = (b) => (b == null ? null : b >= 1024 * 1024 ? `${(b / (1024 * 1024)).toFixed(1)} Mo` : `${Math.round(b / 1024)} Ko`);
const apiErr = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

export default function HeroImagesPanel({ parentId, locales, defaultLanguage, onLocalesChanged }) {
  const { user } = useAuth();
  const readOnly = user?.workspace_role === "viewer";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [drafts, setDrafts] = useState({}); // "imageId:locale" → saisie ALT non sauvegardée
  const [previews, setPreviews] = useState({});
  const previewsRef = useRef({});
  const pollRef = useRef(null);
  const fileRef = useRef(null);
  const [createForm, setCreateForm] = useState({ mode: "shared", locale: "", containsText: false, decorative: false, textContent: "" });

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/content-parents/${parentId}/images`);
      setData(d);
      // Polling UNIQUEMENT pendant GENERATING — jamais permanent.
      const generating = (d.images || []).some((i) => i.status === "GENERATING");
      clearTimeout(pollRef.current);
      if (generating) pollRef.current = setTimeout(() => { load(); onLocalesChanged?.(); }, 3000);
      return d;
    } catch (e) {
      toast.error(apiErr(e, "Chargement des images impossible"));
    }
  }, [parentId, onLocalesChanged]);

  useEffect(() => { load(); return () => clearTimeout(pollRef.current); }, [load]);

  // Init des brouillons ALT depuis le backend SANS écraser une saisie en cours (polling-safe)
  useEffect(() => {
    if (!data) return;
    setDrafts((cur) => {
      const next = { ...cur };
      (data.images || []).forEach((img) => {
        Object.entries(img.alt_by_locale || {}).forEach(([l, v]) => {
          const k = `${img.id}:${l}`;
          if (next[k] === undefined) next[k] = v ?? "";
        });
      });
      return next;
    });
  }, [data]);

  // Previews via l'endpoint backend tenant-safe — JAMAIS d'URL Object Storage reconstruite
  useEffect(() => {
    if (!data) return;
    let cancelled = false;
    (data.images || []).forEach((img) => {
      if (previewsRef.current[img.id]) return;
      if (img.external_url && !img.storage_path) {
        previewsRef.current[img.id] = img.external_url;
        setPreviews({ ...previewsRef.current });
      } else if (img.storage_path && img.status === "READY") {
        api.get(`/content-images/${img.id}/file`, { responseType: "blob" })
          .then((r) => {
            if (cancelled) return;
            previewsRef.current[img.id] = URL.createObjectURL(r.data);
            setPreviews({ ...previewsRef.current });
          })
          .catch(() => {});
      }
    });
    return () => { cancelled = true; };
  }, [data]);
  useEffect(() => () => {
    Object.values(previewsRef.current).forEach((u) => { if (u.startsWith("blob:")) URL.revokeObjectURL(u); });
  }, []);

  if (!data) {
    return <div className="p-6 flex items-center gap-2 text-sm text-slate-500" data-testid="e1-img-loading"><Loader2 className="w-4 h-4 animate-spin" /> Chargement des images…</div>;
  }

  const images = (data.images || []).filter((i) => !i.is_deleted);
  const actives = images.filter((i) => i.role === "ACTIVE");
  const candidates = images.filter((i) => i.role === "CANDIDATE");
  const generating = images.some((i) => i.status === "GENERATING");
  const caps = data.capabilities || {};
  const multiLang = (locales || []).length > 1;
  const localizedMode = createForm.containsText ? "localized" : createForm.mode;
  const maxMo = data.max_upload_bytes ? Math.round(data.max_upload_bytes / (1024 * 1024)) : null;
  const acceptedLabel = "JPEG, PNG, WebP"; // reflet exact du backend (accepted_formats) — SVG jamais proposé
  const accept = (data.accepted_formats || []).join(",");

  const refresh = () => { load(); onLocalesChanged?.(); };

  const saveAlt = async (img, locale) => {
    setBusy(true);
    try {
      await api.patch(`/content-parents/${parentId}/images/${img.id}/alt/${locale}`, { alt: drafts[`${img.id}:${locale}`] ?? "" });
      toast.success(`ALT ${locale} enregistré — les autres langues ne sont pas modifiées`);
      refresh();
    } catch (e) { toast.error(apiErr(e, "Enregistrement de l'ALT impossible")); }
    finally { setBusy(false); }
  };

  const toggleDecorative = async (img, checked) => {
    setBusy(true);
    try {
      await api.patch(`/content-parents/${parentId}/images/${img.id}`, { decorative: checked });
      refresh();
    } catch (e) { toast.error(apiErr(e, "Modification impossible")); }
    finally { setBusy(false); }
  };

  const generate = async () => {
    setBusy(true); setUploadError(null);
    try {
      await api.post(`/content-parents/${parentId}/images/generate`, {
        shared: localizedMode === "shared",
        contains_text: createForm.containsText,
        locale: localizedMode === "localized" ? (createForm.locale || locales?.[0]?.locale) : null,
        text_content: createForm.containsText && createForm.textContent ? createForm.textContent : null,
      });
      toast.info("Génération d'image lancée — l'image actuelle reste active pendant ce temps.");
      load();
    } catch (e) { setUploadError(apiErr(e, "Génération impossible")); }
    finally { setBusy(false); }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploadError(null);
    if (data.max_upload_bytes && file.size > data.max_upload_bytes) {
      setUploadError(`Fichier trop volumineux (maximum ${maxMo} Mo).`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("shared", localizedMode === "shared");
      fd.append("contains_text", createForm.containsText);
      if (localizedMode === "localized") fd.append("locale", createForm.locale || locales?.[0]?.locale || "");
      fd.append("decorative", createForm.decorative);
      await api.post(`/content-parents/${parentId}/images/upload`, fd);
      toast.success("Image téléversée et enregistrée.");
      refresh();
    } catch (e) { setUploadError(apiErr(e, "Ce fichier n'a pas pu être accepté.")); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const promote = async (img) => {
    setBusy(true);
    try {
      await api.post(`/content-parents/${parentId}/images/${img.id}/promote`);
      toast.success("Cette image est maintenant l'image active.");
      refresh();
    } catch (e) { toast.error(apiErr(e, "Promotion impossible")); }
    finally { setBusy(false); }
  };

  const remove = async (img) => {
    const warn = img.role === "ACTIVE"
      ? "Supprimer cette image ?\n\nLa suppression locale ne supprime pas nécessairement l'image déjà publiée sur le site distant."
      : "Supprimer cette image candidate ?";
    if (!window.confirm(warn)) return;
    setBusy(true);
    try {
      const { data: res } = await api.delete(`/content-parents/${parentId}/images/${img.id}`);
      if (res.note) toast.warning(res.note); else toast.success("Image supprimée.");
      refresh();
    } catch (e) { toast.error(apiErr(e, "Suppression impossible")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="e1-img-panel">
      {/* Provider — MANUAL_REQUIRED persistant (dérivé des capabilities à CHAQUE fetch) */}
      {caps.supports_image_alt === false && (
        <div className="border border-orange-200 bg-orange-50 rounded-md p-4 text-xs text-orange-800 space-y-1" data-testid="e1-img-manual-required">
          <div className="font-semibold flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Action manuelle requise — images</div>
          <div>
            Le provider « {caps.provider} » ne permet pas la publication automatique de l&apos;image hero
            ni de ses textes alternatifs. Publiez l&apos;image et les ALT ci-dessous manuellement dans votre CMS.
          </div>
        </div>
      )}
      {!caps.image_generation_configured && (
        <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 flex items-center gap-1.5" data-testid="e1-img-provider-not-configured">
          <Info className="w-3.5 h-3.5" /> La génération d&apos;images IA n&apos;est pas configurée pour ce site. Le téléversement manuel reste disponible.
        </div>
      )}

      {/* Images actives */}
      {actives.length === 0 && candidates.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white rounded-md p-8 text-center" data-testid="e1-img-empty">
          <ImageIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-600 font-medium">Aucune image hero</p>
          <p className="text-xs text-slate-500 mt-1">Générez une image par IA ou téléversez un fichier — rien n&apos;est enregistré tant que le serveur ne l&apos;a pas accepté.</p>
        </div>
      ) : (
        actives.map((img) => (
          <ActiveImageCard key={img.id} img={img} previews={previews} locales={locales}
            defaultLanguage={defaultLanguage} data={data} drafts={drafts} setDrafts={setDrafts}
            saveAlt={saveAlt} toggleDecorative={toggleDecorative} remove={remove}
            readOnly={readOnly} busy={busy} />
        ))
      )}

      {/* Candidates : GENERATING / READY / ERROR — l'active reste visible au-dessus */}
      {candidates.map((img) => (
        <CandidateCard key={img.id} img={img} previews={previews} promote={promote}
          remove={remove} readOnly={readOnly} busy={busy} hasActive={actives.length > 0} />
      ))}

      {/* Actions de création — jamais automatiques, cachées en lecture seule */}
      {!readOnly && (
        <div className="border border-slate-200 rounded-md p-4 space-y-3 bg-white" data-testid="e1-img-create-form">
          <div className="text-xs font-semibold text-slate-700">Nouvelle image hero</div>
          {multiLang && (
            <div className="flex items-center gap-4 text-xs text-slate-700 flex-wrap">
              <label className={`inline-flex items-center gap-1.5 ${createForm.containsText ? "opacity-50" : "cursor-pointer"}`}
                title={createForm.containsText ? "Une image contenant du texte doit être associée à une langue spécifique." : ""}>
                <input type="radio" name="e1-img-mode" data-testid="e1-img-mode-shared" checked={localizedMode === "shared"}
                  disabled={createForm.containsText}
                  onChange={() => setCreateForm((f) => ({ ...f, mode: "shared" }))} />
                Image partagée (toutes les langues, ALT indépendant par langue)
              </label>
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input type="radio" name="e1-img-mode" data-testid="e1-img-mode-localized" checked={localizedMode === "localized"}
                  onChange={() => setCreateForm((f) => ({ ...f, mode: "localized" }))} />
                Image localisée (propre à une langue)
              </label>
            </div>
          )}
          <div className="flex items-center gap-4 text-xs text-slate-700 flex-wrap">
            {(localizedMode === "localized" && multiLang) && (
              <label className="inline-flex items-center gap-1.5">
                Langue :
                <select data-testid="e1-img-locale-select" value={createForm.locale || locales?.[0]?.locale || ""}
                  onChange={(e) => setCreateForm((f) => ({ ...f, locale: e.target.value }))}
                  className="border border-slate-300 rounded px-2 py-1 bg-white font-mono uppercase">
                  {(locales || []).map((l) => <option key={l.locale} value={l.locale}>{l.locale}</option>)}
                </select>
              </label>
            )}
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" data-testid="e1-img-contains-text" checked={createForm.containsText}
                onChange={(e) => setCreateForm((f) => ({ ...f, containsText: e.target.checked, mode: e.target.checked ? "localized" : f.mode }))} />
              L&apos;image contient du texte (impose une image localisée)
            </label>
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" data-testid="e1-img-decorative-new" checked={createForm.decorative}
                onChange={(e) => setCreateForm((f) => ({ ...f, decorative: e.target.checked }))} />
              Image décorative (ALT vide volontaire)
            </label>
          </div>
          {createForm.containsText && (
            <input data-testid="e1-img-text-content" value={createForm.textContent}
              onChange={(e) => setCreateForm((f) => ({ ...f, textContent: e.target.value }))}
              placeholder={`Texte exact à intégrer dans l'image (${createForm.locale || locales?.[0]?.locale || ""})`}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-xs" />
          )}
          <div className="flex items-center gap-2 flex-wrap">
            {caps.image_generation_configured && (
              <button onClick={generate} disabled={busy || generating} data-testid="e1-img-generate-btn"
                className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#001D6B] disabled:opacity-50">
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {actives.length > 0 ? "Régénérer par IA" : "Générer une image (IA)"}
              </button>
            )}
            <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="e1-img-upload-btn"
              className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-50 disabled:opacity-50">
              <Upload className="w-4 h-4" /> Téléverser une image
            </button>
            <input ref={fileRef} type="file" accept={accept} className="hidden" data-testid="e1-img-upload-input"
              onChange={(e) => uploadFile(e.target.files?.[0])} />
            <span className="text-[11px] text-slate-500">Formats acceptés : {acceptedLabel}{maxMo ? ` · ${maxMo} Mo max` : ""}</span>
          </div>
          {uploadError && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="e1-img-upload-error">{uploadError}</div>
          )}
        </div>
      )}
    </div>
  );
}

function ActiveImageCard({ img, previews, locales, defaultLanguage, data, drafts, setDrafts,
                           saveAlt, toggleDecorative, remove, readOnly, busy }) {
  const diags = data.diagnostics?.[img.id] || [];
  const enabledCodes = (locales || []).filter((l) => l.enabled !== false).map((l) => l.locale);
  const multiLang = (locales || []).length > 1;
  const usedBy = Object.entries(data.active_by_locale || {})
    .filter(([k, v]) => v === img.id && enabledCodes.includes(k)).map(([k]) => k);
  const relevant = img.shared_across_locales
    ? (locales || [])
    : (locales || []).filter((l) => l.locale === img.locale);
  const imageDiags = diags.filter((d) => (d.locale || "").includes(","));
  const meta = [
    img.width && img.height ? `${img.width} × ${img.height}` : null,
    img.mime_type, fmtSize(img.file_size),
  ].filter(Boolean).join(" · ");
  const testId = img.shared_across_locales ? "e1-img-active-shared" : `e1-img-active-${img.locale}`;

  return (
    <div className="border border-slate-200 rounded-md p-4 bg-white space-y-3" data-testid={testId}>
      <div className="flex gap-4 flex-wrap md:flex-nowrap">
        <div className="w-full md:w-56 flex-shrink-0">
          {previews[img.id]
            ? <img src={previews[img.id]} alt={`Aperçu de l'image hero ${img.shared_across_locales ? "partagée" : img.locale}`}
                className="rounded-md border border-slate-200 w-full object-cover aspect-video" data-testid={`e1-img-preview-${img.id}`} />
            : <div className="rounded-md border border-slate-200 w-full aspect-video bg-slate-50 flex items-center justify-center text-slate-300"><ImageIcon className="w-6 h-6" /></div>}
        </div>
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
            <span className={`px-2 py-0.5 rounded-full font-semibold ${STATUS_STYLES[img.status] || "bg-slate-100"}`} data-testid={`e1-img-status-${img.id}`}>
              {STATUS_LABELS[img.status] || img.status} · Active
            </span>
            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
              {img.shared_across_locales
                ? (multiLang ? "Partagée entre les langues" : "Image du contenu")
                : `Localisée — ${(img.locale || "").toUpperCase()}`}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{SOURCE_LABELS[img.source] || img.source}</span>
            {img.contains_text
              ? <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid="e1-img-contains-text-badge">Contient du texte{img.locale ? ` (${img.locale})` : ""}</span>
              : <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Sans texte</span>}
            {img.decorative && <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-600" data-testid="e1-img-decorative-badge">Décorative — ALT vide volontaire</span>}
          </div>
          {meta && <div className="text-xs text-slate-500" data-testid={`e1-img-meta-${img.id}`}>{meta}</div>}
          {img.shared_across_locales && usedBy.length > 0 && (
            <div className="text-xs text-slate-500">Utilisée par : <b className="uppercase">{usedBy.join(", ")}</b></div>
          )}
          {!readOnly && (
            <div className="flex items-center gap-3 pt-1 flex-wrap">
              <label className="inline-flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
                <input type="checkbox" checked={!!img.decorative} disabled={busy}
                  data-testid={`e1-img-decorative-toggle-${img.id}`}
                  onChange={(e) => toggleDecorative(img, e.target.checked)} />
                Image décorative (ALT vide volontaire)
              </label>
              <button onClick={() => remove(img)} disabled={busy} data-testid={`e1-img-delete-btn-${img.id}`}
                className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800">
                <Trash2 className="w-3.5 h-3.5" /> Supprimer
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ALT par locale — édition indépendante, backend = source de vérité */}
      <div className="border-t border-slate-100 pt-3 space-y-2">
        <div className="text-xs font-semibold text-slate-700">Texte alternatif par langue <span className="font-normal text-slate-400">— décrit l&apos;image pour l&apos;accessibilité et le SEO</span></div>
        {relevant.map((l) => {
          const key = `${img.id}:${l.locale}`;
          const backendVal = (img.alt_by_locale || {})[l.locale];
          const disabledLang = l.enabled === false;
          // Langue désactivée : données conservées/éditables, mais pas de bruit ALT manquant
          const missing = !img.decorative && !disabledLang &&
            (backendVal === undefined || backendVal === "");
          const rowDiags = disabledLang ? [] : diags.filter((d) => d.locale === l.locale);
          const published = l.publication_status === "PUBLISHED";
          return (
            <div key={l.locale} className="rounded-md border border-slate-100 p-2.5 space-y-1.5" data-testid={`e1-img-alt-row-${l.locale}`}>
              <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
                <span className="font-mono uppercase font-semibold text-slate-700">{l.locale}</span>
                {l.locale === defaultLanguage && <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">Langue principale</span>}
                {l.enabled === false && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500" data-testid={`e1-img-disabled-lang-${l.locale}`}>Langue désactivée</span>}
                {published && (l.image_outdated
                  ? <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold" data-testid={`e1-img-pubstate-${l.locale}`}>À republier</span>
                  : <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold inline-flex items-center gap-1" data-testid={`e1-img-pubstate-${l.locale}`}><CheckCircle2 className="w-3 h-3" /> Publié</span>)}
                {img.decorative
                  ? <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500" data-testid={`e1-img-alt-decorative-${l.locale}`}>ALT vide volontaire</span>
                  : missing && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" data-testid={`e1-img-alt-missing-${l.locale}`}>ALT manquant</span>}
              </div>
              <div className="flex items-center gap-2">
                <input value={img.decorative ? "" : (drafts[key] ?? backendVal ?? "")}
                  onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                  disabled={readOnly || img.decorative || busy}
                  placeholder={img.decorative ? "ALT vide volontaire (image décorative)" : `Texte alternatif ${l.locale}`}
                  data-testid={`e1-img-alt-input-${l.locale}`}
                  className="flex-1 border border-slate-300 rounded-md px-3 py-1.5 text-xs bg-white disabled:bg-slate-50" />
                {!readOnly && !img.decorative && (
                  <button onClick={() => saveAlt(img, l.locale)} disabled={busy} data-testid={`e1-img-alt-save-${l.locale}`}
                    className="text-xs font-semibold text-[#002FA7] border border-[#002FA7]/30 rounded-md px-3 py-1.5 hover:bg-blue-50 disabled:opacity-50">
                    Enregistrer
                  </button>
                )}
              </div>
              {rowDiags.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid={`e1-img-alt-diags-${l.locale}`}>
                  {rowDiags.map((d, i) => (
                    <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded ${d.severity === "warning" ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-slate-50 text-slate-600 border border-slate-200"}`}>
                      {d.message}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {imageDiags.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="e1-img-image-diags">
            {imageDiags.map((d, i) => (
              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">{d.message}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CandidateCard({ img, previews, promote, remove, readOnly, busy, hasActive }) {
  if (img.status === "GENERATING") {
    return (
      <div className="border border-blue-200 bg-blue-50 rounded-md p-4 flex items-center gap-3" data-testid="e1-img-generating">
        <Loader2 className="w-5 h-5 animate-spin text-[#002FA7] flex-shrink-0" />
        <div className="text-sm text-slate-700">
          Nouvelle image en cours de génération…
          {hasActive && <span className="block text-xs text-slate-500 mt-0.5">L&apos;image actuelle reste active tant que la nouvelle n&apos;est pas prête.</span>}
        </div>
      </div>
    );
  }
  if (img.status === "ERROR") {
    return (
      <div className="border border-red-200 bg-red-50 rounded-md p-4 space-y-1.5" data-testid="e1-img-candidate-error">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-medium text-red-800">
              La génération de la nouvelle image a échoué.{hasActive && " L'image actuelle a été conservée."}
            </div>
            {img.error && <div className="text-xs text-red-700 mt-0.5" data-testid="e1-img-candidate-error-detail">{img.error}</div>}
          </div>
          {!readOnly && (
            <button onClick={() => remove(img)} disabled={busy} data-testid={`e1-img-delete-btn-${img.id}`}
              className="text-xs text-red-700 border border-red-300 rounded-md px-2.5 py-1 hover:bg-red-100">Retirer</button>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="border border-emerald-200 bg-emerald-50/50 rounded-md p-4 space-y-2" data-testid="e1-img-candidate-ready">
      <div className="text-xs font-semibold text-emerald-800">Nouvelle image candidate prête</div>
      <div className="flex gap-4 items-start flex-wrap">
        {previews[img.id]
          ? <img src={previews[img.id]} alt="Aperçu de l'image candidate" className="rounded-md border border-emerald-200 w-56 object-cover aspect-video" data-testid={`e1-img-preview-${img.id}`} />
          : <div className="rounded-md border border-emerald-200 w-56 aspect-video bg-white flex items-center justify-center text-slate-300"><ImageIcon className="w-6 h-6" /></div>}
        <div className="space-y-2">
          <div className="text-xs text-slate-600">
            {[img.width && img.height ? `${img.width} × ${img.height}` : null, img.mime_type, fmtSize(img.file_size)].filter(Boolean).join(" · ")}
          </div>
          {!readOnly && (
            <div className="flex items-center gap-2">
              <button onClick={() => promote(img)} disabled={busy} data-testid="e1-img-promote-btn"
                className="inline-flex items-center gap-1.5 bg-emerald-600 text-white px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50">
                <CheckCircle2 className="w-3.5 h-3.5" /> Utiliser cette image
              </button>
              <button onClick={() => remove(img)} disabled={busy} data-testid={`e1-img-delete-btn-${img.id}`}
                className="text-xs text-slate-600 border border-slate-300 rounded-md px-2.5 py-1.5 hover:bg-slate-50">
                Conserver l&apos;actuelle (supprimer la candidate)
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
